<script setup lang="ts">
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import { useProductPricesStore } from '@/stores/product-prices';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import BackstageButton from './BackstageButton.vue';

export interface NavBarItem {
  id: string;
  title: string;
  total: number;
  selected: boolean;
}

export interface Props {
  items: NavBarItem[];
  showQuantities: boolean;
  showWholesalePrice: boolean;
  showExcelUpload?: boolean;
}

const props = defineProps<Props>();

const { shouldShowWholesalePriceOnProductMatrix } = storeToRefs(useProductPricesStore());

const emit = defineEmits<{
  selected: [value: string];
  'toggle-show-wholesale-price': [];
  'open-excel-upload': [];
}>();

let navBarItems = props.items;

watch(
  () => props.items,
  function () {
    navBarItems = props.items;
  },
);

function selectItem(item: NavBarItem) {
  navBarItems.map((navBarItem) => (navBarItem.selected = navBarItem.id === item.id));
  emit('selected', item.id);
}
</script>
<template>
  <nav class="nav-bar">
    <ul class="nav-bar-nav">
      <li
        v-for="item in navBarItems"
        :key="item.id"
        :class="[{ selected: item.selected }]"
        class="nav-item"
        v-on:click="selectItem(item)">
        <div class="title">
          <p>
            {{ item.title }}
          </p>
          <span v-if="showQuantities">{{ '( ' + item.total + ' )' }}</span>
        </div>
      </li>
    </ul>

    <div class="nav-bar-actions">
      <BackstageButton
        v-if="props.showExcelUpload"
        :variant="ButtonVariant.SECONDARY"
        :icon-right="Icons.UPLOAD"
        data-cy="excel-upload-btn"
        @click="emit('open-excel-upload')">
        {{ $t('matrix.excel_upload') }}
      </BackstageButton>

      <BackstageButton
        v-if="shouldShowWholesalePriceOnProductMatrix"
        v-on:click="emit('toggle-show-wholesale-price')"
        :variant="ButtonVariant.SECONDARY"
        id="toggle-show-wholesale-price-button"
        data-cy="toggle-show-wholesale-price-button"
        :icon-right="props.showWholesalePrice ? Icons.EYE_CLOSED : Icons.EYE_OPEN">
        {{ props.showWholesalePrice ? $t('matrix.hide_wholesale_price') : $t('matrix.show_wholesale_price') }}
      </BackstageButton>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-bar-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}
</style>
